import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
} from '@mui/material';

import { useAppSelector } from '../../../../hooks';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../components/V2/Table/type1/TableHeadCustom';
import UserTableRow from './components/UserTableRow';
import TablePaginationActions from '../../../../components/V2/Table/type1/TablePaginationActions';
import {
  getUserListAsAdmin,
  getUsersState,
  IBaseUser,
  TAdminUserListGet,
} from '../../../../store/UserSlice';
import { countriesOptions } from '../../../../constants';
import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../components/V2/Buttons';
import AddUserModal from '../modals/AddUserModal';
import { checkUserPermission } from '../../../../helper';
import { getAuthState } from '../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../store/V2/RolesSlice';
import { logInAsUser } from '../../../../store/AuthSlice/V2/AuthApi';
import { getTenantsState } from '../../../../store/V2/TenantsSlice';
import UserRoleListDropDown, {
  TUserRoleSelectOption,
} from '../../../../components/Inputs/admin/UserRoleListDropDown';

export const searchOptions = [
  { label: 'Name', value: 'name' },
  { label: 'Username', value: 'username' },
  { label: 'Email', value: 'email' },
  { label: 'Phone Number', value: 'phoneNumber' },
  { label: 'Parent Email', value: 'parentEmail' },
  { label: 'Parent Phone Number', value: 'parentPhoneNumber' },
];

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedRole: TUserRoleSelectOption | null;
  selectedCountry?: TSelectOption | null;
  selectedSearchOption?: TSelectOption | undefined;
  searchText?: string;
  isAddModalOpen: boolean;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  role?: string;
  searchOption?: string;
  searchText?: string;
  country?: string;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
  searchOption: searchOptions[0].value,
};

const defaultState: TState = {
  selectedRole: null,
  selectedCountry: null,
  searchText: '',
  selectedSearchOption: searchOptions[0],
  isAddModalOpen: false,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'username',
    label: 'Username',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
  {
    id: 'role',
    label: 'Role',
    sortable: false,
  },
  {
    id: 'firstName',
    label: 'First Name',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
  {
    id: 'lastName',
    label: 'Last Name',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
  {
    id: 'phoneNumber',
    label: 'Mobile Number',
    sortable: false,
    width: 150,
    maxWidth: 150,
  },
  {
    id: 'email',
    label: 'Email',
    sortable: false,
  },
  {
    id: 'createdAt',
    label: 'Registered On',
    sortable: true,
  },
  {
    id: 'action',
    label: 'Action',
    sortable: false,
    align: 'center',
  },
];

const UsersList: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const usersState = useAppSelector(getUsersState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { adminUserList } = usersState;

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState({ ...defaultState });
  const [filterData, setFilterData] = useState({ ...defaultFilterData });

  let usersList: IBaseUser[] = [];
  let totalCount = 0;

  if (adminUserList) {
    totalCount = adminUserList.totalDocs || 0;

    if (adminUserList.list && adminUserList.list.length) {
      usersList = adminUserList.list;
    }
  }

  const createPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminUsers: [USER_PERMISSION_INDIVIDUALS.ADMIN_USERS_CREATE],
    },
  });

  useEffect(() => {
    getUsersListData({ ...defaultFilterData });
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    updateStateAttributes({
      searchText: value,
    });

    getUsersListData({
      ...filterData,
      searchText: value || undefined,
    });
  };

  const getUsersListData = async (filterDataAttributes: TFilterData) => {
    const apiData: TAdminUserListGet = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.searchOption) {
      apiData.searchOption = filterDataAttributes.searchOption;
    }

    if (filterDataAttributes.searchText) {
      apiData.searchText = filterDataAttributes.searchText;
    }

    if (filterDataAttributes.role) {
      apiData.role = filterDataAttributes.role;
    }

    if (filterDataAttributes.country) {
      apiData.country = filterDataAttributes.country;
    }

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    dispatch(getUserListAsAdmin({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getUsersListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getUsersListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getUsersListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleLogInAsUser = (userId: string) => {
    dispatch(logInAsUser(userId));
  };

  return (
    <div>
      <Stack flexDirection="row" padding="20px" alignItems="center">
        <Grid container columnSpacing="16px" rowSpacing="20px">
          <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                size="small"
                value={state.selectedSearchOption}
                options={searchOptions}
                onChange={(event, value) => {
                  updateStateAttributes({
                    selectedSearchOption: value,
                  });

                  getUsersListData({
                    ...filterData,
                    searchOption: value ? value.value : undefined,
                  });
                }}
                popupIcon={TPAutoCompleteArrowIconComponent}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Search By"
                    placeholder="Search By"
                  />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
            <FormControl fullWidth>
              <TPTextField
                {...textFieldProps}
                name="email"
                size="small"
                label="Search"
                placeholder="Search"
                value={state.searchText}
                onChange={handleInputChange}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
            <FormControl fullWidth>
              <UserRoleListDropDown
                selectedItem={state.selectedRole}
                componentProps={{
                  fullWidth: true,
                  isOptionEqualToValue: (option, value) =>
                    option.label === value.value,
                }}
                textFieldProps={{
                  label: 'Role',
                  placeholder: 'Select',
                  ...textFieldProps,
                }}
                handleChange={(e, value) => {
                  updateStateAttributes({
                    selectedRole: value,
                  });

                  getUsersListData({
                    ...filterData,
                    role: value ? value.value : undefined,
                  });
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
            <FormControl fullWidth>
              <Autocomplete
                size="small"
                value={state.selectedCountry}
                options={countriesOptions}
                onChange={(event, value) => {
                  updateStateAttributes({
                    selectedCountry: value,
                  });

                  getUsersListData({
                    ...filterData,
                    country: value ? value.value : undefined,
                  });
                }}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                popupIcon={TPAutoCompleteArrowIconComponent}
                renderInput={(params) => (
                  <TPTextField
                    {...params}
                    {...textFieldProps}
                    size="small"
                    label="Country"
                    placeholder="Country"
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
        {createPermission && (
          <DarkButton
            onClick={() =>
              updateStateAttributes({
                isAddModalOpen: true,
              })
            }
            sx={{ ...defaultButtonStyles, minWidth: '110px' }}
          >
            Add User
          </DarkButton>
        )}
      </Stack>
      <TableContainer
        sx={{ position: 'relative', maxHeight: 'calc(100vh - 300px)' }}
      >
        <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={tableData.order}
            orderBy={tableData.orderBy}
            headLabels={tableHeaderLabels}
            rowCount={0}
            numSelected={tableData.selected.length}
            onSort={handleSort}
          />

          <TableBody>
            {usersList.map((row) => (
              <UserTableRow
                key={row._id}
                userData={row}
                handleLogInAsUser={handleLogInAsUser}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={defaultRowsPerPageOptions}
          component="div"
          count={totalCount}
          rowsPerPage={tableData.rowsPerPage}
          page={tableData.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleRowsPerPageChange}
          ActionsComponent={TablePaginationActions}
        />

        <FormControlLabel
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
          control={
            <Switch
              checked={tableData.dense}
              onChange={tableData.onChangeDense}
            />
          }
        />
      </Box>
      {/* Modals */}
      {state.isAddModalOpen ? (
        <AddUserModal
          open={state.isAddModalOpen}
          onClose={() =>
            updateStateAttributes({
              isAddModalOpen: false,
            })
          }
        />
      ) : null}
    </div>
  );
};

export default UsersList;
